import React, { useState, useEffect, useCallback, useRef } from "react";
import type * as CSS from "csstype";

import {
    IconButton,
    Button,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { Add, KeyboardTab, DragIndicator } from "@mui/icons-material";

import {
    useSideEntityController,
    useSnackbarController,
    Entity,
    CollectionTable,
    useNavigation,
    EntityReference,
    buildCollection,
    CollectionSize,
    useDataSource,
    WhereFilterOp,
} from "@camberi/firecms";

// eslint-disable-next-line import/no-unresolved
import { Market, Service } from "../types";
import serviceSchema from "../schemas/service";
import marketSchema from "../schemas/markets";
import { serviceCallbacks } from "../callbacks";

interface DragEntityData {
    entityId: string;
    entityName: string;
    entityData: {
        id: string;
        values: any;
        path: string;
    };
    sourceIndex: number;
    timestamp?: number;
}

const CATEGORIES = [
    "Attractions",
    "Dining", 
    "Fitness",
    "Grooming",
    "Health Services",
    "Home Services",
    "Kids Corner",
    "Beauty & Wellness",
    "Pets",
    "Retail",
    "Transportation"
];

export function ServiceView() {
    const servicesRef = useRef<Entity<Service>[]>([]);
    const [markets, setMarkets] = React.useState([
        {
            name: "Markets Loading...",
            id: "ALL",
            location: { lat: -27.1, lon: 120.1 },
        },
    ] as Market[]);
    const [market, setMarket] = React.useState(markets[0]);
    const [category, setCategory] = React.useState<string>("");
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [services, setServices] = useState<Entity<Service>[]>([]);
    const [collectionKey, setCollectionKey] = useState(0);
    const [isUpdating, setIsUpdating] = useState(false);
    const [draggedRowIndex, setDraggedRowIndex] = useState<number | null>(null);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
    const [draggedEntity, setDraggedEntity] = useState<Entity<Service> | null>(null);
    const [draggedEntityId, setDraggedEntityId] = useState<string | null>(null);
    const [shouldRenderTable, setShouldRenderTable] = useState(true);

    // Scroll-related refs for continuous animation
    const scrollAnimationRef = useRef<number | null>(null);
    const scrollSpeedRef = useRef(0);
    const scrollDirectionRef = useRef(0);
    const scrollElementRef = useRef<HTMLElement | null>(null);

    const stopScrolling = useCallback(() => {
        scrollSpeedRef.current = 0;
        scrollDirectionRef.current = 0;
        scrollElementRef.current = null;
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
            scrollAnimationRef.current = null;
        }
    }, []);

    // Continuous scroll animation
    const animateScroll = useCallback(() => {
        if (!scrollElementRef.current || scrollSpeedRef.current === 0) {
            scrollAnimationRef.current = null;
            return;
        }

        const element = scrollElementRef.current;
        const scrollAmount = scrollSpeedRef.current * scrollDirectionRef.current;
        
        const canScrollUp = element.scrollTop > 0;
        const canScrollDown = element.scrollTop < element.scrollHeight - element.clientHeight;
        
        if ((scrollDirectionRef.current === -1 && canScrollUp) || 
            (scrollDirectionRef.current === 1 && canScrollDown)) {
            
            element.scrollBy({
                top: scrollAmount,
                behavior: 'auto'
            });
        }

        if (scrollSpeedRef.current > 0) {
            scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        } else {
            scrollAnimationRef.current = null;
        }
    }, []);

    const startScrolling = useCallback((element: HTMLElement, speed: number, direction: number) => {
        scrollElementRef.current = element;
        scrollSpeedRef.current = speed;
        scrollDirectionRef.current = direction;
        
        if (!scrollAnimationRef.current) {
            scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        }
    }, [animateScroll]);

    // Build collection with category filter only when category is selected
    const getCollection = (marketId: string, categoryValue?: string) => {
        let initialFilter: any = undefined;

        if (marketId !== "ALL") {
            initialFilter = {
                market: [
                    "==" as WhereFilterOp,
                    new EntityReference(marketId ?? "", "/cms-markets/"),
                ],
            };

            // Only adding category filter if category is selected
            if (categoryValue && categoryValue !== "") {
                initialFilter.category = [
                    "==" as WhereFilterOp,
                    categoryValue,
                ];
            }
        }

        return buildCollection({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            initialFilter,
            path: "cms-services",
            schema: serviceSchema,
            name: "Services",
            description: "Create and manage services for each market.",
            initialSort: marketId === "ALL" ? undefined : ["order", "asc"],
            permissions: ({ authController }) => ({
                edit: true,
                create: true,
                delete: authController.extra.roles.includes("admin"),
            }),
            callbacks: serviceCallbacks,
        });
    };

    const [collection, setCollection] = React.useState(
        getCollection(market?.id || "ALL", category)
    );

    const handleMarketChange = (event: SelectChangeEvent) => {
        const newMarketSelected = markets.filter(
            (val) => val.id === event.target.value
        )[0];

        setMarket(newMarketSelected);
        setCategory(""); // Reset category when market changes
        setCollectionKey(prev => prev + 1);
        setCollection(getCollection(newMarketSelected?.id || "ALL", ""));
    };

    const handleCategoryChange = (event: SelectChangeEvent) => {
        const newCategory = event.target.value as string;
        setCategory(newCategory);
        // Force table refresh when category changes
        setCollectionKey(prev => prev + 1);
        setCollection(getCollection(market?.id || "ALL", newCategory));
    };

    const sideEntityController = useSideEntityController();
    const snackbarController = useSnackbarController();
    const navContext = useNavigation();
    const path = "/cms-services";
    const collectionResolver = navContext.getCollectionResolver("cms-services");

    if (!collectionResolver) throw Error("Collection resolver is undefined!!!");
    const { schemaResolver } = collectionResolver;

    const dataSource = useDataSource();

    useEffect(() => {
        servicesRef.current = services;
    }, [services]);

    useEffect(() => {
        dataSource
            .fetchCollection({
                path: "cms-markets/",
                schema: marketSchema,
            })
            .then(async (marketList) => {
                setMarkets([
                    {
                        name: "All Markets",
                        id: "ALL",
                        location: { lat: -27.1, lon: 120.1 },
                    },
                    ...marketList.map((market): Market => {
                        return {
                            name: market.values.name,
                            id: market.id,
                            location: market.values.location,
                        };
                    }),
                ]);
            });
    }, []);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                let filter: any = undefined;
                if (market.id !== "ALL") {
                    filter = {
                        market: [
                            "==" as WhereFilterOp,
                            new EntityReference(market.id || "", "/cms-markets/"),
                        ],
                    };

                    // Adding category filter if category is selected 
                    if (category && category !== "") {
                        filter.category = [
                            "==" as WhereFilterOp,
                            category,
                        ];
                    }
                }

                const serviceList = await dataSource.fetchCollection({
                    path: "cms-services",
                    schema: serviceSchema,
                    filter,
                });

                // const emails = serviceList.map(entity => entity.values.emailAddress); 
                // console.log(JSON.stringify(emails, null, 2));
 
                const sortedServices = serviceList.sort((a, b) => {
                    const orderA = (a.values as any).order || 0;
                    const orderB = (b.values as any).order || 0;
                    return orderA - orderB;
                }) as Entity<Service>[];
                
                setServices(sortedServices);
            } catch (error) {
                console.error("Error fetching services:", error);
            }
        };

        fetchServices();
    }, [market, category, dataSource, collectionKey]);

    const forceTableRefresh = useCallback(async () => {
        setShouldRenderTable(false);
        await new Promise(resolve => setTimeout(resolve, 100));
        setCollectionKey(Date.now());
        setCollection(getCollection(market?.id || "ALL", category));
        await new Promise(resolve => setTimeout(resolve, 100));
        setShouldRenderTable(true);
    }, [market, category]);

    useEffect(() => {
        if (services.length > 0) {
            setTimeout(() => {
                const dragHandles = document.querySelectorAll('.drag-handle');
                dragHandles.forEach((handle, i) => {
                    const entityId = handle.getAttribute('data-entity-id');
                    const entityName = handle.getAttribute('data-entity-name');
                });
            }, 500);
        }
    }, [services, shouldRenderTable]);

    // Create a collection that will be used with our filtered data
    const getFilteredCollection = useCallback(() => {
        return getCollection(market?.id || "ALL", category);
    }, [market?.id, category]);

    // Update collection when category changes
    useEffect(() => {
        setCollection(getFilteredCollection());
    }, [getFilteredCollection]);

    // Drag and drop implementation
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || !shouldRenderTable) {
            return;
        }

        const handleDragStart = (e: DragEvent) => {
            const target = e.target as HTMLElement;
            const dragHandleElement = target.closest('.drag-handle');
            
            if (!dragHandleElement) {
                e.preventDefault();
                return;
            }

            const dragHandle = dragHandleElement as HTMLElement;
            const entityId = dragHandle.getAttribute('data-entity-id');
            const entityName = dragHandle.getAttribute('data-entity-name');
            
            if (!entityId) {
                e.preventDefault();
                return;
            }

            const currentServicesArray = servicesRef.current.length > 0 ? servicesRef.current : services;
            const draggedEntity = currentServicesArray.find(s => s.id === entityId);
            const draggedRowIndex = currentServicesArray.findIndex(s => s.id === entityId);

            if (!draggedEntity || draggedRowIndex === -1) {
                e.preventDefault();
                return;
            }

            setDraggedEntityId(entityId);
            setDraggedEntity(draggedEntity);
            setDraggedRowIndex(draggedRowIndex);

            if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                
                const dragData: DragEntityData = {
                    entityId: entityId,
                    entityName: entityName || entityId,
                    entityData: {
                        id: draggedEntity.id,
                        values: draggedEntity.values,
                        path: draggedEntity.path
                    },
                    sourceIndex: draggedRowIndex,
                    timestamp: Date.now()
                };
                
                e.dataTransfer.setData('text/html', JSON.stringify(dragData));
            }
        };

        const handleDragOver = (e: DragEvent) => {
            if (draggedRowIndex === null) {
                return;
            }
            e.preventDefault();
            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = "move";
            }
            
            const target = e.target as HTMLElement;
            const row = target.closest("[role='row']") as HTMLElement;
            if (!row || row.closest(".BaseTable__header")) {
                return;
            }
            
            const allRows = Array.from(
                container.querySelectorAll("[role='row']")
            ).filter((r) => !r.closest(".BaseTable__header"));
            const hoverIndex = allRows.indexOf(row);
            if (hoverIndex !== -1 && hoverIndex !== hoveredRowIndex) {
                setHoveredRowIndex(hoverIndex);
            }
            
            if (container) {
                const mouseY = e.clientY;
                const SCROLL_ZONE_HEIGHT = 100;
                const SCROLL_SPEED = 8; 
                
                let scrollableElement: HTMLElement = container;
                
                const possibleScrollers = container.querySelectorAll('*');
                for (let i = 0; i < possibleScrollers.length; i++) {
                    const el = possibleScrollers[i] as HTMLElement;
                    const computedStyle = getComputedStyle(el);
                    if (el.scrollHeight > el.clientHeight && 
                        (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll')) {
                        scrollableElement = el;
                        break;
                    }
                }
                
                const containerRect = scrollableElement.getBoundingClientRect();
                const containerTop = containerRect.top;
                const containerBottom = containerRect.bottom;
                
                const distanceFromContainerTop = mouseY - containerTop;
                const distanceFromContainerBottom = containerBottom - mouseY;
                
                let shouldScroll = false;
                let scrollDirection = 0;
                
                if (mouseY >= containerTop && mouseY <= containerBottom) {
                    // Top scroll zone
                    if (distanceFromContainerTop < SCROLL_ZONE_HEIGHT && distanceFromContainerTop >= 0) {
                        if (scrollableElement.scrollTop > 0) {
                            shouldScroll = true;
                            scrollDirection = -1;
                        }
                    }
                    // Bottom scroll zone
                    else if (distanceFromContainerBottom < SCROLL_ZONE_HEIGHT && distanceFromContainerBottom >= 0) {
                        if (scrollableElement.scrollTop < 
                            scrollableElement.scrollHeight - scrollableElement.clientHeight) {
                            shouldScroll = true;
                            scrollDirection = 1;
                        }
                    }
                }
                
                if (shouldScroll) {
                    startScrolling(scrollableElement, SCROLL_SPEED, scrollDirection);
                } else {
                    stopScrolling();
                }
            }
        };

        const handleDragLeave = (e: DragEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[role="row"]')) {
                setHoveredRowIndex(null);
            }
            stopScrolling();
        };

        const handleDrop = async (e: DragEvent) => {
            e.preventDefault();
            stopScrolling();
            
            if (isUpdating) {
                return;
            }

            let draggedEntityData: DragEntityData | null = null;
            let currentDraggedEntity: Entity<Service> | null = null;
            let currentDraggedIndex = -1;

            try {
                const transferData = e.dataTransfer!.getData('text/html');
                if (transferData) {
                    const parsedData = JSON.parse(transferData) as DragEntityData;
                    draggedEntityData = parsedData;
                }
            } catch (error) {
                console.log(error)
            }

            if (!draggedEntityData && draggedEntity && draggedEntityId) {
                draggedEntityData = {
                    entityId: draggedEntityId,
                    entityName: draggedEntity.values?.name || draggedEntityId,
                    entityData: {
                        id: draggedEntity.id,
                        values: draggedEntity.values,
                        path: draggedEntity.path
                    },
                    sourceIndex: draggedRowIndex || 0
                };
            }

            if (!draggedEntityData || !draggedEntityData.entityId) {
                return;
            }

            setIsUpdating(true);

            const currentServicesArray = 
                servicesRef.current.length > 0 ? 
                servicesRef.current :
                services;
            
            const entityStillExists = currentServicesArray.find(
                s => s.id === draggedEntityData!.entityId
            );
            if (!entityStillExists) {
                setIsUpdating(false);
                return;
            }

            currentDraggedEntity = entityStillExists;
            currentDraggedIndex = currentServicesArray.findIndex(
                s => s.id === draggedEntityData!.entityId
            );

            if (currentDraggedIndex === -1) {
                setIsUpdating(false);
                return;
            }

            const target = e.target as HTMLElement;
            let dropRow = target.closest('[role="row"]') as HTMLElement;
            
            if (!dropRow || dropRow.closest('.BaseTable__header')) {
                const allRows = Array.from(
                    tableContainerRef.current!.querySelectorAll('[role="row"]')
                ).filter(r => !r.closest('.BaseTable__header'));
                
                const dropY = e.clientY;
                let closestRow = null;
                let closestDistance = Infinity;
                
                for (const row of allRows) {
                    const rowRect = (row as HTMLElement).getBoundingClientRect();
                    const rowCenterY = rowRect.top + rowRect.height / 2;
                    const distance = Math.abs(dropY - rowCenterY);
                    
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestRow = row;
                    }
                }
                
                if (closestRow) {
                    dropRow = closestRow as HTMLElement;
                } else {
                    setIsUpdating(false);
                    return;
                }
            }

            const dropRowDragHandle = dropRow.querySelector(
                '.drag-handle[data-entity-id]'
            ) as HTMLElement;
            
            let dropTargetEntity: Entity<Service> | null = null;
            let dropIndex = -1;

            if (dropRowDragHandle) {
                const dropEntityId = dropRowDragHandle.getAttribute('data-entity-id');
                
                if (dropEntityId) {
                    dropTargetEntity = currentServicesArray.find(s => s.id === dropEntityId) || null;
                    dropIndex = currentServicesArray.findIndex(s => s.id === dropEntityId);
                }
            }

            if (!dropTargetEntity || dropIndex === -1) {
                const allRows = Array.from(tableContainerRef.current!.querySelectorAll('[role="row"]'))
                    .filter(r => !r.closest('.BaseTable__header'));
                const visualDropIndex = allRows.indexOf(dropRow);
                
                if (visualDropIndex !== -1 && visualDropIndex < currentServicesArray.length) {
                    dropTargetEntity = currentServicesArray[visualDropIndex];
                    dropIndex = visualDropIndex;
                }
            }

            if (dropIndex === -1 || !dropTargetEntity) {
                setIsUpdating(false);
                return;
            }

            if (currentDraggedIndex !== dropIndex) {
                const draggedName = currentDraggedEntity.values.name || currentDraggedEntity.id;
                const direction = currentDraggedIndex < dropIndex ? "DOWN" : "UP";
                
                const newServices = [...currentServicesArray];
                const [movedService] = newServices.splice(currentDraggedIndex, 1);
                newServices.splice(dropIndex, 0, movedService);
                
                try {
                    const updatePromises = newServices.map((service: Entity<Service>, index: number) => {
                        return dataSource.saveEntity({
                            path: "cms-services",
                            entityId: service.id,
                            values: {
                                ...service.values,
                                order: index,
                            } as any,
                            schema: serviceSchema,
                            status: "existing",
                        });
                    });

                    await Promise.all(updatePromises);
                    
                    const categoryText = category ? ` in ${category}` : "";
                    snackbarController.open({
                        type: "success",
                        message: `
                            Operation completed: "${draggedName}" moved ${direction} 
                            from position ${currentDraggedIndex + 1} to ${dropIndex + 1}${categoryText}
                        `
                    });
                    
                    await forceTableRefresh();
                    setIsUpdating(false);
                    
                } catch (error) {
                    console.error("DATABASE UPDATE ERROR:", error);
                    
                    snackbarController.open({
                        type: "error",
                        message: "Error updating service order"
                    });
                    
                    setIsUpdating(false);
                }
            } else {
                setIsUpdating(false);
            }
        };

        const handleDragEnd = (e: DragEvent) => { 
            setDraggedRowIndex(null);
            setHoveredRowIndex(null);
            setDraggedEntity(null);
            setDraggedEntityId(null); 
            stopScrolling();
        };

        if (!isUpdating) {
            container.addEventListener('dragstart', handleDragStart);
            container.addEventListener('dragover', handleDragOver);
            container.addEventListener('dragleave', handleDragLeave);
            container.addEventListener('drop', handleDrop);
            container.addEventListener('dragend', handleDragEnd);
        }

        return () => {
            container.removeEventListener('dragstart', handleDragStart);
            container.removeEventListener('dragover', handleDragOver);
            container.removeEventListener('dragleave', handleDragLeave);
            container.removeEventListener('drop', handleDrop);
            container.removeEventListener('dragend', handleDragEnd);
            
            // Cleanup scroll animation
            if (scrollAnimationRef.current) {
                cancelAnimationFrame(scrollAnimationRef.current);
                scrollAnimationRef.current = null;
            }
        };
    }, [services, dataSource, snackbarController, market, category, hoveredRowIndex, 
        draggedRowIndex, draggedEntity, isUpdating, shouldRenderTable, 
        forceTableRefresh, stopScrolling, startScrolling, animateScroll]);

    const onEntityClick = useCallback(
        (entity: Entity<Service>) => {
            return sideEntityController.open({
                entityId: entity.id,
                path,
                permissions: collection.permissions,
                schema: collection.schema,
                subcollections: collection.subcollections,
                callbacks: collection.callbacks,
                overrideSchemaRegistry: false,
            });
        },
        [path, collection, sideEntityController]
    );

    const onNewClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            return sideEntityController.open({
                path,
                permissions: collection.permissions,
                schema: collection.schema,
                subcollections: collection.subcollections,
                callbacks: collection.callbacks,
                overrideSchemaRegistry: false,
            });
        },
        [path, collection, sideEntityController]
    );

    const toolbarActionsBuilder = useCallback(() => {
        const addButton = (
            <Button
                onClick={onNewClick}
                startIcon={<Add />}
                size="large"
                variant="contained"
                color="primary"
                disabled={isUpdating}
            >
                Add {collection.schema.name}
            </Button>
        );

        return <>{addButton}</>;
    }, [collection, onNewClick, isUpdating]);

    const tableRowActionsBuilder = useCallback(
        ({
            entity,
            size,
        }: {
            entity: Entity<Service>;
            size: CollectionSize;
        }) => {
            const currentRowIndex = services.findIndex(s => s.id === entity.id);
            const isDragged = draggedRowIndex === currentRowIndex;
            const isHovered = hoveredRowIndex === currentRowIndex;
            // Only show drag handle if market is selected (not "ALL") AND category is selected
            const showDragHandle = market.id !== "ALL" && category !== "";
            
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    {showDragHandle && (
                        <Box
                            className="drag-handle"
                            draggable={!isUpdating}
                            data-entity-id={entity.id}
                            data-entity-name={entity.values.name || entity.id}
                            sx={{
                                cursor: isUpdating ? 'not-allowed' : (isDragged ? 'grabbing' : 'grab'),
                                padding: '8px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isDragged ? '#1976d2' : isHovered ? '#e3f2fd' : '#f5f5f5',
                                borderRadius: '4px',
                                border: isDragged ? '2px solid #1976d2' : 
                                       isHovered ? '2px solid #2196f3' : '1px solid #ddd',
                                minWidth: '32px',
                                height: '32px',
                                transform: isDragged ? 'scale(1.05)' : 'scale(1)',
                                transition: 'all 0.2s ease',
                                boxShadow: isDragged ? '0 4px 8px rgba(0,0,0,0.2)' : 'none',
                                opacity: isUpdating ? 0.5 : 1,
                                '&:hover': !isUpdating ? {
                                    backgroundColor: isDragged ? '#1976d2' : '#e3f2fd',
                                    borderColor: '#2196f3',
                                    transform: 'scale(1.05)',
                                } : {},
                                '&:active': !isUpdating ? {
                                    cursor: 'grabbing',
                                    transform: 'scale(0.95)',
                                } : {}
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <DragIndicator 
                                sx={{ 
                                    fontSize: '16px', 
                                    color: isDragged ? '#fff' : isHovered ? '#2196f3' : '#666',
                                    transition: 'color 0.2s ease',
                                }} 
                            />
                        </Box>
                    )}
                    
                    <IconButton 
                        size="large" 
                        onClick={() => onEntityClick(entity)}
                        disabled={isUpdating}
                        sx={{
                            opacity: isDragged ? 0.5 : 1,
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <KeyboardTab />
                    </IconButton>
                </Box>
            );
        },
        [
            onEntityClick, services, 
            draggedRowIndex, hoveredRowIndex, 
            isUpdating, market.id, category
        ]
    );

    const selectorStyles: CSS.Properties = {
        maxWidth: "300px",
        margin: "20px auto",
        padding: "0 20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    };

    const containerStyle: CSS.Properties = {
        width: "100%",
        height: "100%",
        overflow: "auto",
        flexGrow: 1,
        maxHeight: "calc(100% - 150px)", // Adjusted for additional selector
        position: "relative",
    };

    return (
        <React.Fragment>
            <div className="selectors" style={selectorStyles}>
                <FormControl fullWidth>
                    <InputLabel id="market-select-label">Market</InputLabel>
                    <Select
                        labelId="market-select-label"
                        id="market-select"
                        value={market.id}
                        label="Market"
                        onChange={handleMarketChange}
                        disabled={isUpdating}
                    >
                        {markets.map((market, i) => (
                            <MenuItem value={market.id} key={i}>
                                {market.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Category selector - only show if a specific market is selected */}
                {market.id !== "ALL" && (
                    <FormControl fullWidth>
                        <InputLabel id="category-select-label">Category</InputLabel>
                        <Select
                            labelId="category-select-label"
                            id="category-select"
                            value={category}
                            label="Category"
                            onChange={handleCategoryChange}
                            disabled={isUpdating}
                        >
                            <MenuItem value="">
                                <em>All Categories</em>
                            </MenuItem>
                            {CATEGORIES.map((cat, i) => (
                                <MenuItem value={cat} key={i}>
                                    {cat}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </div>

            <div ref={tableContainerRef} style={containerStyle}>
                {isUpdating && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bgcolor="rgba(255, 255, 255, 0.8)"
                        zIndex={1000}
                    >
                        <CircularProgress />
                    </Box>
                )}
                
                {shouldRenderTable && (
                    <CollectionTable
                        path="cms-services"
                        collection={collection}
                        key={`${market.id || "ALL"}-${category}-${collectionKey}`}
                        inlineEditing={false}
                        schemaResolver={schemaResolver}
                        onEntityClick={onEntityClick}
                        toolbarActionsBuilder={toolbarActionsBuilder}
                        tableRowActionsBuilder={tableRowActionsBuilder}
                    />
                )}
                
                {!shouldRenderTable && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        height="200px"
                    >
                        <CircularProgress />
                    </Box>
                )}
            </div>
        </React.Fragment>
    );
}